import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["as", "children", "className", "condensed", "fullWidth", "narrow"],
    _excluded2 = ["as", "className", "children", "mode"];

/**
 * Copyright IBM Corp. 2016, 2018
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { usePrefix } from '../../internal/usePrefix';
import { GridSettings, useGridSettings } from './GridContext';

function CSSGrid(_ref) {
  var _cx;

  var _ref$as = _ref.as,
      BaseComponent = _ref$as === void 0 ? 'div' : _ref$as,
      children = _ref.children,
      customClassName = _ref.className,
      _ref$condensed = _ref.condensed,
      condensed = _ref$condensed === void 0 ? false : _ref$condensed,
      _ref$fullWidth = _ref.fullWidth,
      fullWidth = _ref$fullWidth === void 0 ? false : _ref$fullWidth,
      _ref$narrow = _ref.narrow,
      narrow = _ref$narrow === void 0 ? false : _ref$narrow,
      rest = _objectWithoutProperties(_ref, _excluded);

  var prefix = usePrefix();

  var _useGridSettings = useGridSettings(),
      subgrid = _useGridSettings.subgrid;

  var mode = 'wide';

  if (narrow) {
    mode = 'narrow';
  } else if (condensed) {
    mode = 'condensed';
  }

  if (subgrid) {
    return /*#__PURE__*/React.createElement(GridSettings, {
      mode: "css-grid",
      subgrid: true
    }, /*#__PURE__*/React.createElement(Subgrid, _extends({
      as: BaseComponent,
      className: customClassName,
      mode: mode
    }, rest), children));
  }

  var className = cx(customClassName, (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--css-grid"), true), _defineProperty(_cx, "".concat(prefix, "--css-grid--condensed"), mode === 'condensed'), _defineProperty(_cx, "".concat(prefix, "--css-grid--narrow"), mode === 'narrow'), _defineProperty(_cx, "".concat(prefix, "--css-grid--full-width"), fullWidth), _cx));
  return /*#__PURE__*/React.createElement(GridSettings, {
    mode: "css-grid",
    subgrid: true
  }, /*#__PURE__*/React.createElement(BaseComponent, _extends({
    className: className
  }, rest), children));
}

CSSGrid.propTypes = {
  /**
   * Provide a custom element to render instead of the default <div>
   */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),

  /**
   * Pass in content that will be rendered within the `Grid`
   */
  children: PropTypes.node,

  /**
   * Specify a custom className to be applied to the `Grid`
   */
  className: PropTypes.string,

  /**
   * Collapse the gutter to 1px. Useful for fluid layouts.
   * Rows have 1px of margin between them to match gutter.
   */
  condensed: PropTypes.bool,

  /**
   * Remove the default max width that the grid has set
   */
  fullWidth: PropTypes.bool,

  /**
   * Container hangs 16px into the gutter. Useful for
   * typographic alignment with and without containers.
   */
  narrow: PropTypes.bool
};

function Subgrid(_ref2) {
  var _cx2;

  var _ref2$as = _ref2.as,
      BaseComponent = _ref2$as === void 0 ? 'div' : _ref2$as,
      customClassName = _ref2.className,
      children = _ref2.children,
      mode = _ref2.mode,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var prefix = usePrefix();
  var className = cx(customClassName, (_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "--subgrid"), true), _defineProperty(_cx2, "".concat(prefix, "--subgrid--condensed"), mode === 'condensed'), _defineProperty(_cx2, "".concat(prefix, "--subgrid--narrow"), mode === 'narrow'), _defineProperty(_cx2, "".concat(prefix, "--subgrid--wide"), mode === 'wide'), _cx2));
  return /*#__PURE__*/React.createElement(BaseComponent, _extends({}, rest, {
    className: className
  }), children);
}

Subgrid.propTypes = {
  /**
   * Provide a custom element to render instead of the default <div>
   */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),

  /**
   * Pass in content that will be rendered within the `Subgrid`
   */
  children: PropTypes.node,

  /**
   * Specify a custom className to be applied to the `Subgrid`
   */
  className: PropTypes.string,

  /**
   * Specify the grid mode for the subgrid
   */
  mode: PropTypes.oneOf(['wide', 'narrow', 'condensed'])
};
export { CSSGrid };